import React from 'react';
import { Button, Descriptions, Skeleton, Space, Spin, Tag, Typography } from 'antd';
import Drawer from '../../../components/AppDrawer';
import { useCampaign } from '../hooks/useCampaign';
import { CAMPAIGN_STATUS_LABELS } from '../constants';
import type { CampaignStatus } from '../types';
import { useLeads } from '../../leads/hooks/useLeads';
import AuditProperties from '../../../components/AuditProperties';

const { Title, Text } = Typography;

interface CampaignViewDrawerProps {
  open: boolean;
  onClose: () => void;
  campaignId: string | null;
  onEdit?: (id: string) => void;
}

function formatDate(s: string | null | undefined): string {
  if (s == null || s === '') return '-';
  try {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString();
  } catch {
    return '-';
  }
}

function formatNumber(value: number | null | undefined): string {
  if (value == null) return '-';
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
}

const statusColors: Record<CampaignStatus, string> = {
  0: 'default',
  1: 'blue',
  2: 'green',
  3: 'red',
};

const CampaignViewDrawer: React.FC<CampaignViewDrawerProps> = ({ open, onClose, campaignId, onEdit }) => {
  const { data: campaign, isLoading } = useCampaign(open ? campaignId : null);

  const { data: leadsData, isLoading: loadingLeads } = useLeads(
    { pageNumber: 1, pageSize: 1, campaignId: campaignId ?? undefined },
    { enabled: open && campaignId != null },
  );
  const leadCount = leadsData?.totalCount ?? 0;

  return (
    <Drawer
      title="Campaign details"
      placement="right"
      size="large"
      open={open}
      onClose={onClose}
      destroyOnClose
      extra={
        campaign && onEdit ? (
          <Button type="primary" onClick={() => onEdit(campaign.id)}>
            Edit
          </Button>
        ) : undefined
      }
    >
      {isLoading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : !campaign ? (
        <Text type="secondary">Campaign not found.</Text>
      ) : (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              {campaign.name}
            </Title>
            {campaign.description && (
              <Text type="secondary">{campaign.description}</Text>
            )}
          </div>

          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Status">
              <Tag color={statusColors[campaign.status]}>
                {CAMPAIGN_STATUS_LABELS[campaign.status] ?? '-'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Channel">
              {campaign.channelLabel ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Start date">
              {formatDate(campaign.startDateUtc)}
            </Descriptions.Item>
            <Descriptions.Item label="End date">
              {formatDate(campaign.endDateUtc)}
            </Descriptions.Item>
            <Descriptions.Item label="Budget">
              {formatNumber(campaign.budget)}
            </Descriptions.Item>
            <Descriptions.Item label="Target goal">
              {formatNumber(campaign.targetedAmountGoal)}
            </Descriptions.Item>
            <Descriptions.Item label="Leads">
              {loadingLeads ? <Spin size="small" /> : leadCount}
            </Descriptions.Item>
            <Descriptions.Item label="Tags">
              {campaign.tags.length > 0
                ? campaign.tags.map((t) => <Tag key={t}>{t}</Tag>)
                : '-'}
            </Descriptions.Item>
          </Descriptions>

          <AuditProperties
            values={{
              createdAtUtc: campaign.createdAtUtc,
              createdByDisplayName: campaign.createdByDisplayName,
              createdByUserId: campaign.createdByUserId,
              lastModifiedAtUtc: campaign.lastModifiedAtUtc,
              modifiedByDisplayName: campaign.modifiedByDisplayName,
              modifiedByUserId: campaign.modifiedByUserId,
            }}
          />
        </Space>
      )}
    </Drawer>
  );
};

export default CampaignViewDrawer;
